import { useState, useEffect, useCallback } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Field, FieldGroup, FieldLabel } from '../components/ui/field';
import { Input } from '../components/ui/input';
import PageHeader from '../components/PageHeader';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-hot-toast';
import { User, Mail, Phone, Building, Save } from 'lucide-react';
import api from '../config/api';
import { useNavigate } from 'react-router-dom';

const UserProfile = () => {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [profile, setProfile] = useState({
    fullName: '',
    email: '',
    phone: '',
    department: '',
    company: '',
  });

  // 获取用户首字母用于头像显示
  const getUserInitials = () => {
    if (!user?.username) return '?';
    return user.username.charAt(0).toUpperCase();
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProfile(prev => ({ ...prev, [name]: value }));
  };

  // 获取用户个人资料
  const fetchUserProfile = useCallback(async () => {
    if (!token) {
      toast.error('您需要登录才能访问此页面');
      logout();
      navigate('/login');
      return;
    }
    
    setIsLoading(true);
    try {
      const response = await api.get('/api/user/profile');
      
      if (response.data.success && response.data.profile) {
        setProfile({
          fullName: response.data.profile.fullName || '',
          email: response.data.profile.email || '',
          phone: response.data.profile.phone || '',
          department: response.data.profile.department || '',
          company: response.data.profile.company || '',
        });
      }
    } catch (error) {
      // console.error('获取用户资料失败:', error);
      
      // 检查是否是认证错误
      if (error.isSessionExpired) {
        return;
      }

      if (error.response?.status === 403) {
        toast.error('登录状态已过期，请重新登录');
        logout('expired');
        navigate('/login');
      } else {
        toast.error('获取用户资料失败');
      }
    } finally {
      setIsLoading(false);
    }
  }, [logout, navigate, token]);

  // 组件挂载时获取用户资料
  useEffect(() => {
    if (token) {
      fetchUserProfile();
    } else {
      toast.error('您需要登录才能访问此页面');
      navigate('/login');
    }
  }, [fetchUserProfile, navigate, token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!token) {
      toast.error('登录状态已失效，请重新登录');
      logout();
      navigate('/login');
      return;
    }
    
    setIsLoading(true);
    try {
      const response = await api.put('/api/user/profile', profile);
      
      if (response.data.success) {
        toast.success('个人资料已更新');
        setIsEditing(false);
      } else {
        throw new Error(response.data.error || '更新失败');
      }
    } catch (error) {
      // console.error('更新个人资料失败:', error);
      
      // 检查是否是认证错误
      if (error.isSessionExpired) {
        return;
      }

      if (error.response?.status === 403) {
        toast.error('登录状态已过期，请重新登录');
        logout('expired');
        navigate('/login');
      } else {
        toast.error(error.response?.data?.error || '更新个人资料失败');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="page-stack profile-page">
      <PageHeader eyebrow="OPERATOR PROFILE" title="个人资料" description="维护操作员身份、联系方式与所属组织信息。" icon={User} badge={isEditing ? '编辑中' : '只读'} />
      <div className="profile-layout">
        <Card className="profile-summary-card h-fit">
          <CardContent className="flex flex-col items-center p-6 text-center">
            <Avatar className="profile-avatar mb-4 size-24 border-4 border-background shadow-md">
              <AvatarFallback className="bg-primary text-xl font-bold text-primary-foreground">{getUserInitials()}</AvatarFallback>
            </Avatar>
            <h2 className="text-xl font-semibold">{user?.username}</h2>
            <Badge variant="info" className="mt-2">{user?.role === 'admin' ? '系统管理员' : '授权操作员'}</Badge>
            <div className="profile-status my-5 w-full border-t border-dashed border-border pt-5 text-left">
              <p className="text-xs font-semibold text-muted-foreground">账户状态</p>
              <p className="mt-2 text-sm">身份信息已连接至当前会话。</p>
            </div>
            <Button variant={isEditing ? 'secondary' : 'outline'} className="w-full" onClick={() => setIsEditing(!isEditing)} disabled={isLoading}>
              {isEditing ? '取消编辑' : '编辑个人资料'}
            </Button>
          </CardContent>
        </Card>

        <Card className="profile-details-card">
          <CardHeader>
            <CardTitle>详细信息</CardTitle>
            <CardDescription>{isEditing ? '完成修改后保存，资料将同步到当前账户。' : '点击左侧编辑按钮后可修改以下字段。'}</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit}>
              <FieldGroup>
                <Field>
                  <FieldLabel htmlFor="fullName"><User />姓名</FieldLabel>
                  <Input id="fullName" name="fullName" value={profile.fullName} onChange={handleInputChange} disabled={!isEditing || isLoading} placeholder="输入姓名" />
                </Field>
                <Field>
                  <FieldLabel htmlFor="email"><Mail />电子邮箱</FieldLabel>
                  <Input id="email" name="email" type="email" value={profile.email} onChange={handleInputChange} disabled={!isEditing || isLoading} placeholder="输入邮箱地址" />
                </Field>
                <div className="grid gap-5 md:grid-cols-2">
                  <Field>
                    <FieldLabel htmlFor="phone"><Phone />联系电话</FieldLabel>
                    <Input id="phone" name="phone" value={profile.phone} onChange={handleInputChange} disabled={!isEditing || isLoading} placeholder="输入联系电话" />
                  </Field>
                  <Field>
                    <FieldLabel htmlFor="department"><Building />部门</FieldLabel>
                    <Input id="department" name="department" value={profile.department} onChange={handleInputChange} disabled={!isEditing || isLoading} placeholder="输入所属部门" />
                  </Field>
                </div>
                <Field>
                  <FieldLabel htmlFor="company"><Building />公司 / 组织</FieldLabel>
                  <Input id="company" name="company" value={profile.company} onChange={handleInputChange} disabled={!isEditing || isLoading} placeholder="输入公司或组织名称" />
                </Field>
                {isEditing && <div className="flex justify-end pt-1"><Button type="submit" disabled={isLoading}><Save data-icon="inline-start" />{isLoading ? '正在保存…' : '保存更改'}</Button></div>}
              </FieldGroup>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default UserProfile;
